import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Container, Element, Heading, Section } from 'react-bulma-components'
import { setTranslations } from 'react-i18nify'
import Layout from '../../components/layout/layout'
import { Translate } from '../../utils/translations'
import { campaign } from '..'
import { useRecordInspection } from './api'
import { altText } from './alt-text'
import ComparisonPair from './comparison-pair'

/**
 * Inspection instructions, ending in the two outcome buttons.
 *
 * The video and the reference images sit where the brief places them: the video
 * introduces the check, and each thumb-press step is followed by the pair
 * showing what a sound and a failed gate look like after that step.
 *
 * TODO: replace with the real embed once CheckRockyFil.mp4 is on YouTube.
 * The source file is HEVC, which Chrome and Firefox cannot reliably decode, so
 * it must not be served directly.
 */
const VIDEO_EMBED_URL = ''

/** Placeholder button labels — the sheet has no rows for them yet. */
const OUTCOME_BUTTONS = [
  { outcome: 'ok', label: 'OK' },
  { outcome: 'nok', label: 'NOT OK' },
] as const

const SimondProcedurePage: React.FC = () => {
  setTranslations(campaign.presentationLocales)
  const { lang } = useParams()
  const navigate = useNavigate()
  const recordInspection = useRecordInspection()

  /**
   * Record the self-reported inspection result, then show the matching advice.
   *
   * The recording is deliberately not awaited. It is anonymous reporting for
   * Simond, while the advice on the next screen is safety information — a slow
   * or failing request must never delay or withhold it. The request survives
   * this component unmounting.
   *
   * @param outcome - Whether the wire gate stayed seated
   */
  const handleOutcome = (outcome: 'ok' | 'nok') => {
    recordInspection.mutate({
      data: { result: outcome, language: lang ?? 'en' },
    })

    navigate(`/${lang}/result/${outcome}`)
  }

  return (
    <Layout>
      <Element className="simond">
        <Section>
          <Container className="simond__column">
            <Element className="simond__warning" mb={5} data-testid="stop-using-warning">
              <Translate value="procedure.stopUsing" />
            </Element>

            <Heading size={4} className="simond__title" mb={5}>
              <Translate value="procedure.title" />
            </Heading>

            {VIDEO_EMBED_URL ? (
              <Element className="simond__video" mb={6} data-testid="inspection-video">
                <iframe
                  src={VIDEO_EMBED_URL}
                  title={altText('video', lang)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Element>
            ) : (
              <Element className="simond__video simond__video--pending" mb={6} data-testid="inspection-video-pending">
                Inspection video — awaiting upload
              </Element>
            )}

            <Element mb={5}>
              <Translate value="procedure.intro" />
            </Element>

            <Element className="simond__step" mb={4} data-testid="procedure-step-1">
              <span className="simond__step-number">1</span>
              <Translate value="procedure.step1" />
            </Element>
            <ComparisonPair
              okImageName="push1_ok"
              nokImageName="push1_nok"
              okAlt={altText('push1Ok', lang)}
              nokAlt={altText('push1Nok', lang)}
            />

            <Element className="simond__step" mb={4} data-testid="procedure-step-2">
              <span className="simond__step-number">2</span>
              <Translate value="procedure.step2" />
            </Element>
            <ComparisonPair
              okImageName="push2_ok"
              nokImageName="push2_nok"
              okAlt={altText('push2Ok', lang)}
              nokAlt={altText('push2Nok', lang)}
            />

            <Element className="simond__outcomes" mt={6}>
              {OUTCOME_BUTTONS.map(({ outcome, label }) => (
                <Button
                  key={outcome}
                  size="large"
                  className={`simond__outcome simond__outcome--${outcome}`}
                  data-testid={`outcome-button-${outcome}`}
                  onClick={() => handleOutcome(outcome)}
                >
                  {label}
                </Button>
              ))}
            </Element>
          </Container>
        </Section>
      </Element>
    </Layout>
  )
}

export default SimondProcedurePage
